import axios from 'axios'
import { BFF_URL } from './config'

// Single axios instance pointed at the Veralis BFF.
// withCredentials: the session lives in an httpOnly cookie, so every call has
// to carry it — including the LAN/dev case where the SPA and the BFF are on
// different ports and the browser would otherwise omit it.
export const bff = axios.create({
  baseURL: BFF_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

// Double-submit CSRF. The server sets a readable `veralis_csrf` cookie at login
// and requires it echoed in a header on cookie-authenticated writes: a
// cross-site page can make the browser send the session cookie, but cannot read
// this one to copy it. Read per request — a fresh login rotates the value.
const csrfCookie = () =>
  document.cookie.split('; ').find((c) => c.startsWith('veralis_csrf='))?.split('=')[1]

const UNSAFE = new Set(['post', 'put', 'patch', 'delete'])

bff.interceptors.request.use((cfg) => {
  if (UNSAFE.has(String(cfg.method).toLowerCase())) {
    const token = csrfCookie()
    if (token) cfg.headers['X-Veralis-Csrf'] = token
  }
  return cfg
})

// A 401 anywhere means the session is gone (expired, revoked, or the account
// was disabled) — the whole app has to fall back to the login screen, not just
// the one call that noticed. Broadcast it; the auth hook listens and re-probes.
// A 403 with password_change_required does the same, so a forced change cannot
// be walked around by staying on an already-loaded tab.
export const AUTH_EVENT = 'veralis:auth-changed'

bff.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status
    const data = err?.response?.data
    if (status === 401 || (status === 403 && data?.password_change_required)) {
      window.dispatchEvent(new CustomEvent(AUTH_EVENT))
    }
    return Promise.reject(err)
  }
)

// Normalize BFF/engine errors (the BFF returns { error, hint }) to an Error
// carrying the hint, so UI can show both.
export function toClientError(err) {
  const data = err?.response?.data
  const e = new Error(data?.error || err?.message || 'Request failed')
  e.hint = data?.hint
  e.status = err?.response?.status
  return e
}

export default bff
